import { Currency, DevelopersService, FormatType, Material, Style } from 'api';
import { GetState, SetState } from 'zustand';
import { AppState } from './appstate';
import { Option } from '@swd_admin/cdc-viewer';

export const setStyle = async (
  styleId: string,
  get: GetState<AppState>,
  set: SetState<AppState>
) => {
  const productId = get().styleProduct.product?.id;
  if (!productId) {
    throw new Error('productid not defined');
  }
  const currency = get().uState?.currency || Currency.USD;
  const s = await DevelopersService.getStyle(productId, styleId, currency);

  set((state) => ({
    styleProduct: { style: s, product: state.styleProduct.product },
    uState: {
      parameters: [],
      ...s,
      dimensions: {
        depth: s.defaultConfiguration.style.depth,
        width: s.defaultConfiguration.style.width,
        wallHeight: s.defaultConfiguration.style.wallHeight,
      },
      overhang: s.defaultConfiguration.style.overhang,
      hasConcretePad: s.defaultConfiguration.style.hasConcretePad || false,
      format: FormatType.PRECUT,
      currency: currency,
      materials: {
        trim: s.defaultConfiguration.style.trim,
        roofing: s.defaultConfiguration.style.roofing,
        siding: s.defaultConfiguration.style.siding,
      },
      bomOnlyOptions: (s.defaultConfiguration?.style?.options || [])
        .filter((opt: any) => {
          if (!opt.ghModelJson) return true;
          const apiOption = s.options.find((o) => o.id === opt.definitionId);
          if (!apiOption) return false;
          return (
            !['roof', 'doors', 'windows'].includes(apiOption.optionTypeId) &&
            !s.features.some((f) => f.id === opt.definitionId)
          );
        })
        .map(({ definitionId, id, parentId }: any) => ({
          definitionId,
          id,
          parentId,
        })),
    },
    dicts: {
      ...state.dicts,
      ...getStyleDicts(s),
    },
  }));
};

export const setProduct = async (
  productId: string,
  set: SetState<AppState>
) => {
  try {
    const [product, materials] = await Promise.all([
      DevelopersService.getProductById(productId),
      DevelopersService.getMaterialsForProduct(productId),
    ]);

    set((state) => ({
      ...state,
      styleProduct: {
        ...state.styleProduct,
        product: { ...product, ...materials },
      },
      dicts: {
        ...state.dicts,
        ...getProductDicts(materials.materials),
      },
    }));
  } catch (error) {
    throw new Error(`Error getting product or materials: ${error}`);
  }
};

export const loadConfiguration = async (
  configurationId: string,
  productId: string,
  currency: Currency,
  set: SetState<AppState>
) => {
  const configuration = await DevelopersService.getConfiguration(
    configurationId,
    productId,
    currency as Currency
  );
  const { style } = configuration;

  const [product, materials] = await Promise.all([
    DevelopersService.getProductById(productId),
    DevelopersService.getMaterialsForProduct(productId),
  ]);
  const s = await DevelopersService.getStyle(
    style.productId,
    style.id,
    currency
  );

  set((state) => ({
    configurationId: configuration?.id,
    styleProduct: {
      style: { ...s, defaultConfiguration: configuration },
      product: { ...product, ...materials },
    },
    uState: {
      parameters: [],
      ...s,
      dimensions: {
        depth: configuration.style.depth,
        width: configuration.style.width,
        wallHeight: configuration.style.wallHeight,
      },
      overhang: configuration.style.overhang,
      hasConcretePad: configuration.style.hasConcretePad || false,
      format: FormatType.PRECUT,
      currency: currency,
      materials: {
        trim: configuration.style.trim,
        roofing: configuration.style.roofing,
        siding: configuration.style.siding,
      },
      background: configuration.style.defaultBackground,
      bomOnlyOptions: configuration.style.options
        .filter((opt: any) => {
          if (!opt.ghModelJson) return true; // classic BOM-only
          // Include material_upgrades/post_and_beam with ghModelJson (Urban Pavilion)
          const apiOption = s.options.find((o) => o.id === opt.definitionId);
          if (!apiOption) return false;
          return (
            !['roof', 'doors', 'windows'].includes(apiOption.optionTypeId) &&
            !s.features.some((f) => f.id === opt.definitionId)
          );
        })
        .map(({ definitionId, id, parentId }: any) => ({
          definitionId,
          id,
          parentId,
        })),
    },
    dicts: {
      ...state.dicts,
      ...getStyleDicts(s),
      ...getProductDicts(materials.materials),
    },
  }));
};

export function getStyleDicts(s: Style) {
  const allOptions: Option[] = [...s!.features, ...s!.options];

  const dbIdToShapeDiverId = allOptions.reduce(
    (res: { [key: string]: string }, option) => {
      const { id, orderCode } = option;
      if (res[id]) {
        console.warn(
          `options with ids ${orderCode} and ${res[id]} have the same db id ${id}`
        );
      }
      res[id] = orderCode;
      return res;
    },
    {}
  );

  const shapeDiverIdToDbId = Object.entries(dbIdToShapeDiverId).reduce(
    (res: { [key: string]: string }, [key, value]) => {
      res[value] = key;
      return res;
    },
    {}
  );

  return { dbIdToShapeDiverId, shapeDiverIdToDbId };
}

export function getProductDicts(materials: Array<Material>) {
  const materialsDbIdToShapeDiverId = materials.reduce(
    (res: { [key: string]: string }, material) => {
      const { id, orderCode } = material;
      if (res[id]) {
        console.warn(
          `materials with ids ${orderCode} and ${res[id]} have the same db id ${id}`
        );
      }
      res[id] = orderCode;
      return res;
    },
    {}
  );

  return { materialsDbIdToShapeDiverId };
}

export const updateExtraheightAndOverhang = async (
  styleId: string,
  width: number,
  depth: number,
  get: GetState<AppState>,
  set: SetState<AppState>
) => {
  if (styleId && width && depth) {
    try {
      const res = await DevelopersService.getExtraheightAndOverhang(
        width.toString(),
        depth.toString()
      );

      set((state) => ({
        ...state,
        styleProduct: {
          ...state.styleProduct,
          style: {
            ...(state.styleProduct.style as Style),
            overhangs: res.overhangs,
            wallHeights: res.wallHeights,
          },
        },
      }));
    } catch (e) {}
  }
};
